import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Skeleton,
  Alert,
  Pagination,
} from '@mui/material';
import {
  PhotoCamera as PhotoIcon,
  Science as ScienceIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import { portalApi, ServiceHistoryItem } from '../services/portalApi';
import { formatStatus } from '../utils/formatStatus';
import { formatServiceDuration } from '../utils/formatServiceDuration';
import { useTranslation } from 'react-i18next';
import { i18n } from '../i18n';

export const ServiceHistory: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [services, setServices] = useState<ServiceHistoryItem[]>([]);
  const [total, setTotal] = useState(0);
  const [showTechnicianAndTiming, setShowTechnicianAndTiming] = useState(false);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const limit = 10;

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await portalApi.getServiceHistory({
          limit,
          offset: (page - 1) * limit,
        });
        setServices(response.items);
        setTotal(response.total);
        setShowTechnicianAndTiming(response.showTechnicianAndTiming ?? false);
      } catch (err: any) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [page]);

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString(i18n.resolvedLanguage === 'es' ? 'es-MX' : 'en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const totalPages = Math.ceil(total / limit);

  const formatTime = (dateStr: string) =>
    new Date(dateStr).toLocaleTimeString(i18n.resolvedLanguage === 'es' ? 'es-MX' : 'en-US', {
      hour: 'numeric',
      minute: '2-digit',
    });

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        {t('Service History')}
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        {t('View your complete service history and reports')}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box>
          {[1, 2, 3, 4, 5].map((i) => (
            <Card key={i} sx={{ mb: 2 }}>
              <CardContent>
                <Skeleton width="30%" height={28} />
                <Skeleton width="60%" height={24} />
                <Skeleton width="40%" height={20} />
              </CardContent>
            </Card>
          ))}
        </Box>
      ) : services.length === 0 ? (
        <Card>
          <CardContent sx={{ py: 6, textAlign: 'center' }}>
            <Typography variant="h6" color="text.secondary">
              {t('No service history yet')}
            </Typography>
            <Typography color="text.secondary" sx={{ mt: 1 }}>
              {t('Your service records will appear here after your first visit')}
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <>
          {services.map((service) => (
            <Card
              key={service.id}
              sx={{
                mb: 2,
                cursor: 'pointer',
                transition: 'box-shadow 0.2s',
                '&:hover': { boxShadow: 3 },
              }}
              onClick={() => navigate(`/service-history/${service.id}`)}
            >
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                      <Typography variant="h6">
                        {formatDate(service.serviceDate)}
                      </Typography>
                      <Chip
                        label={t(formatStatus(service.status))}
                        size="small"
                        color={service.status === 'completed' ? 'success' : service.status === 'skipped' ? 'warning' : 'default'}
                      />
                    </Box>

                    {service.logType === 'inspection' && <Typography fontWeight={600} sx={{ mb: 1 }}>{t(service.reportTitle || 'Pool Inspection Report')}</Typography>}
                    <Typography color="text.secondary" sx={{ mb: 1 }}>
                      {service.property.name || service.property.address}
                      {service.bodyOfWater && ` • ${service.bodyOfWater.name}`}
                    </Typography>

                    {showTechnicianAndTiming && service.technicianName && (
                      <Typography variant="body2" color="text.secondary">
                        {t('Technician')}: {service.technicianName}
                      </Typography>
                    )}

                    {showTechnicianAndTiming && service.arrivedAt && service.completedAt && (
                      <Typography variant="body2" color="text.secondary">
                        {t('Service time')}: {formatTime(service.arrivedAt)} - {formatTime(service.completedAt)}
                        {formatServiceDuration(service.durationMinutes)}
                      </Typography>
                    )}

                    <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                      {service.photoCount > 0 && (
                        <Chip
                          icon={<PhotoIcon />}
                          label={`${service.photoCount} ${t(service.photoCount > 1 ? 'photos' : 'photo')}`}
                          size="small"
                          variant="outlined"
                        />
                      )}
                      {service.hasChemicalReading && (
                        <Chip
                          icon={<ScienceIcon />}
                          label={t('Chemical readings')}
                          size="small"
                          variant="outlined"
                        />
                      )}
                    </Box>

                    {service.techNotes && (
                      <Typography
                        variant="body2"
                        sx={{
                          mt: 2,
                          p: 1.5,
                          bgcolor: 'grey.50',
                          borderRadius: 1,
                          fontStyle: 'italic',
                        }}
                      >
                        "{service.techNotes.substring(0, 150)}{service.techNotes.length > 150 ? '...' : ''}"
                      </Typography>
                    )}
                  </Box>

                  <ChevronRightIcon sx={{ color: 'text.secondary', ml: 2 }} />
                </Box>
              </CardContent>
            </Card>
          ))}

          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(_, value) => setPage(value)}
                color="primary"
              />
            </Box>
          )}
        </>
      )}
    </Box>
  );
};
